import { inject, Injectable } from '@angular/core';
import { forkJoin, map, Observable, of } from 'rxjs';

import { BaseEditService, Search } from 'tms-lib';
import { CustomerAddress, CustomerAddressEdit, SupplierAddress } from '../../../models';
import { CustomerAddressService } from './customer-address.service';
import { ShipperInterfaceHeaderService } from './shipper-interface-header.service';

/**
 * A manifest's Delivery Address box (HFW testing points Oct 2026, AC-33): the manifest shipper's own
 * addresses (`id2`) first, then the first despatch note's customer's (`id`), in one list. Everything else -
 * edit, create - stays the customer address service's, as the box behaved before.
 */
@Injectable({
  providedIn: 'root',
})
export class ManifestDeliveryAddressService extends BaseEditService<
  CustomerAddress | SupplierAddress,
  CustomerAddressEdit
> {
  private readonly customerAddressService = inject(CustomerAddressService);
  private readonly shipperInterfaceHeaderService = inject(ShipperInterfaceHeaderService);

  constructor() {
    super('CustomerAddress', $localize`Customer Address`);
  }

  override search(model?: Search): Observable<(CustomerAddress | SupplierAddress)[]> {
    const shipperId = model?.id2;
    // A manifest with no despatch notes has no customer: then only the shipper's addresses are offered,
    // rather than every customer's.
    const customerAddresses =
      model?.id || !shipperId
        ? this.customerAddressService.search({ ...model, id2: undefined })
        : of([] as CustomerAddress[]);
    // No shipper id: "All Addresses" is ticked (the search window clears both ids), or there is no shipper.
    if (!shipperId) {
      return customerAddresses;
    }

    return forkJoin([
      this.shipperInterfaceHeaderService.manifestShipperAddressSearch({ id: shipperId, search: model.search }),
      customerAddresses,
    ]).pipe(
      map(([shipperAddresses, customerAddressResults]: [SupplierAddress[], CustomerAddress[]]) => [
        ...shipperAddresses,
        ...customerAddressResults,
      ]),
    );
  }
}
